import "./index.scss";
import {
  AlgoliaHits,
  AlgoliaSearch,
  AlgoliaStateResults,
} from "@wise-emotions/react-algolia";
import { ButtonIcon } from "@wise-emotions/react-button";
import { GeneralMediumSearch } from "@wise-emotions/react-icons";
import { GetTranslationsObj, Translations } from "types/types";
import { Hit, StateResultsProvided } from "react-instantsearch-core";
import { Link, navigate } from "gatsby";
import { getTranslations } from "helpers/contenful";
import { useMenu } from "utilities/MenuProvider";
import { useMinCharsSearch } from "context/SiteProvider";
import ItemResult from "components/Search/ItemResult";
import Message from "components/Search/Message";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Strip from "components/Strip";
import parse from "html-react-parser";

export interface PropsLight {
  text?: string;
  lang: string;
  translations?: Translations[];
  searchPageUrl: string;
}

const SearchLight = ({
  translations,
  lang,
  searchPageUrl,
  text,
}: PropsLight) => {
  const { searchOpen, toggleSearch } = useMenu();
  const searchInput = useRef(null);
  const memoizedTranslations = useMemo(() => {
    // TO DO: check translations ts
    return getTranslations(
      translations as Translations[],
    ) as GetTranslationsObj;
  }, [translations]);
  const minChars = useMinCharsSearch();
  const textSearch = text ? parse(text) : "";

  const [inputValue, setInputValue] = useState("");

  const currentLangPath = `/${lang}/`;

  const onChange = (val: string) => {
    setInputValue(val);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`${searchPageUrl}?s=${inputValue}`);
  };

  useEffect(() => {
    if (searchInput.current && searchOpen) {
      // @ts-expect-error
      searchInput.current.focus();
    }
  }, [searchInput, searchOpen]);

  const onKeyDown = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.key === "Escape" && searchOpen && toggleSearch) {
        toggleSearch();
        setInputValue("");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchOpen],
  );

  useEffect(() => {
    if (searchOpen) {
      document.addEventListener("keydown", onKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOpen]);

  return (
    <div className="SearchLight">
      <Strip
        xsWrapContent="sm"
        paddingBottom="md"
        hasPaddingWrapMobile
        customClass="SearchLight__section-top"
      >
        <form className="SearchForm__form" onSubmit={submitSearch}>
          <div className="SearchForm__input">
            <AlgoliaSearch
              enableSubmit
              searchProps={{
                label: memoizedTranslations["search-input-label"],
                placeholder: memoizedTranslations["search-input-placeholder"],
                name: "search",
                id: "search-page",
                cancelLabel: memoizedTranslations["search-input-cancel-label"],
                value: inputValue,
                onChange: (evt: ChangeEvent<HTMLInputElement>) =>
                  onChange(evt.currentTarget.value),
                onCancel: () => setInputValue(""),
              }}
              inputRef={searchInput}
            />
          </div>
          <div className="SearchForm__button" data-testid="search-button">
            <ButtonIcon
              ariaLabel="search"
              size="lg"
              type="submit"
              aria-label={memoizedTranslations["search-button-title"]}
            >
              <GeneralMediumSearch />
            </ButtonIcon>
          </div>
        </form>
      </Strip>

      <Strip
        xsWrapContent="sm"
        hasPaddingWrapMobile
        customClass="SearchLight__section-content"
      >
        {inputValue.length >= minChars && (
          <div data-testid="results" className="SearchLight__results">
            <AlgoliaStateResults>
              {({ searching, searchResults, error }: StateResultsProvided) => {
                return (
                  <>
                    {searching && (
                      <div className="SearchLoader">
                        <div className="SearchLoader__img" />
                      </div>
                    )}
                    {searchResults && searchResults.nbHits === 0 && !error && (
                      <div className="SearchLight__message">
                        <Message
                          abstract={
                            memoizedTranslations["search-not-found-text"]
                          }
                          title={memoizedTranslations["search-not-found-title"]}
                          iconName="general_medium_search"
                        />
                      </div>
                    )}
                    {error && (
                      <div className="SearchLight__message">
                        <Message
                          abstract={
                            memoizedTranslations["search-unavailable-text"]
                          }
                          title={
                            memoizedTranslations["search-unavailable-title"]
                          }
                          iconName="general_medium_sad"
                        />
                      </div>
                    )}
                  </>
                );
              }}
            </AlgoliaStateResults>
            <div className="SearchLight__list">
              <AlgoliaHits>
                {({ title: tit, abstract, __position: index, slug }: Hit) => {
                  const link = currentLangPath + slug;
                  return (
                    <ItemResult
                      link={link}
                      title={tit}
                      abstract={abstract}
                      index={+index}
                    />
                  );
                }}
              </AlgoliaHits>
            </div>
            <AlgoliaStateResults>
              {({ searchResults }: StateResultsProvided) =>
                searchResults?.nbHits > 0 ? (
                  <div className="SearchLight__results__recap">
                    <Link
                      className="SearchLight__results__link link-cta link-cta-label link-cta-label--accent"
                      to={`${searchPageUrl}?s=${inputValue}`}
                    >
                      {`${memoizedTranslations["search-view-all-results"]} (${searchResults.nbHits})`}
                    </Link>
                  </div>
                ) : (
                  <div />
                )
              }
            </AlgoliaStateResults>
          </div>
        )}
        <div className="SearchLight__text editorial">{textSearch}</div>
      </Strip>
    </div>
  );
};

export default SearchLight;
